/**
 * One row per attachment: preview, kuvateksti, size, remove.
 *
 * Each item gets its own caption because the report's description covers the
 * report, not the picture. A run of three screenshots is unreadable without a
 * line saying what each one shows.
 *
 * A video shows its poster frame with a duration badge, or a placeholder when the
 * browser could not decode the file well enough to grab a frame.
 */

import { Input } from "@/components/ui/input";
import { formatDuration } from "@/lib/feedbackMeta";
import { CloseIcon, ImageIcon } from "@/lib/icons";
import { cn } from "@/lib/utils";
import type { UploadedAttachment } from "@/hooks/useFeedbackAttachmentUpload";

interface AttachmentTrayProps {
  items: UploadedAttachment[];
  onCaptionChange: (id: string, caption: string) => void;
  onRemove: (id: string) => void;
}

function formatSize(bytes: number): string {
  return `${Math.max(1, Math.round(bytes / 1024))} kt`;
}

export function AttachmentTray({
  items,
  onCaptionChange,
  onRemove,
}: AttachmentTrayProps) {
  if (items.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      {items.map((item) => (
        <div
          key={item.id}
          className="grid grid-cols-[6.5rem_minmax(0,1fr)_auto] items-center gap-3 rounded-lg border bg-card p-2"
        >
          <span className="relative block h-[68px] w-[104px]">
            {item.previewUrl ? (
              <img
                src={item.previewUrl}
                alt={item.filename}
                className="h-full w-full rounded border object-cover"
              />
            ) : (
              // Video whose codec the browser could not decode. It still
              // uploaded and still plays — only the preview is missing.
              <span className="grid h-full w-full place-items-center rounded border border-dashed bg-muted">
                <ImageIcon className="h-5 w-5 text-muted-foreground" />
              </span>
            )}
            {item.mediaType === "VIDEO" && (
              <span
                className={cn(
                  "absolute bottom-1 right-1 rounded bg-black/70 px-1 py-0.5",
                  "font-mono text-[9px] font-bold leading-none text-white",
                )}
              >
                {formatDuration(item.durationSeconds) || "video"}
              </span>
            )}
          </span>
          <div className="flex min-w-0 flex-col gap-1">
            <Input
              value={item.caption}
              onChange={(event) => onCaptionChange(item.id, event.target.value)}
              placeholder={
                item.mediaType === "VIDEO"
                  ? "Kuvateksti — mitä videolla näkyy?"
                  : "Kuvateksti — mitä tässä kuvassa näkyy?"
              }
              aria-label={`Kuvateksti liitteelle ${item.filename}`}
              maxLength={300}
              className="h-9"
            />
            <span className="truncate font-mono text-[10.5px] text-muted-foreground">
              {item.filename} · {formatSize(item.sizeBytes)}
              {item.mediaType === "VIDEO" && item.durationSeconds
                ? ` · ${formatDuration(item.durationSeconds)}`
                : ""}
            </span>
          </div>
          <button
            type="button"
            onClick={() => onRemove(item.id)}
            aria-label={`Poista ${item.filename}`}
            className="grid h-7 w-7 place-items-center rounded-md border text-muted-foreground hover:border-destructive hover:text-destructive"
          >
            <CloseIcon className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  );
}
